import React, { useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, useMediaQuery, InputLabel, Card, CardContent, Box, FormControl, Tooltip, Link, Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import { useDivisionOptions, useTeamOptions, useDocsetIdList, useReportTypeOptions, useProjectOptions, usePowerTrainOptions, useGradeOptions, useShimukeOptions, useLotOptions, useFreeOptions, invalidateDocsetIdListCache, ENLIGHT_TEAM_COLUMN_ABSOLUTE_LIST } from '../../../state/master';
import { apiClient } from '../../../lib/apiClient';
import ReplayIcon from '@mui/icons-material/Replay';
import { Snackbar } from '../../common/Snackbar/Snackbar';
import { computeBodyType } from '../../common/computeBodyType';
import CommonSelect from '../../common/commonSelect/CommonSelect';
import ConfirmDialog from '../../common/ConfirmDialog';
import DocumentMismatchPanel from './DocumentMismatchPanel';
import '../ReadOnlyTable.css';

type Props = {
  open: boolean;
  division: number;
  team: number;
  onClose: () => void;
  // deprecated: チーム設定は onAcceptTeam を使用
  onAccept?: (payload: { division: number; team: number; enlight_team: string; enlight_subteam: string | null; docset_id: number | null }) => void;
  // チーム設定モードの確定時に呼ばれる
  onAcceptTeam?: (payload: { division: number; team: number; enlight_team: string; enlight_subteam: string | null; docset_id: number | null }) => void;
  // カラム設定モードの確定時に呼ばれる
  onAcceptColumn?: (payload: {
    division: number;
    team: number;
    enlight_team: string;
    enlight_subteam: string | null;
    bindings: Record<string, string>;
  }) => void;
  initialMode?: 'team' | 'column';
  enlight_team?: string | null;
  enlight_subteam?: string | null;
  // TEAM_DOCSET_MANAGE.docset_id（親から共有された、既に保存済みの連携先docset_id）
  enlight_docset_id?: number | null;
  canEdit?: boolean;
};

const EnlightTeamSettingDialog: React.FC<Props> = ({ open, division, team, onClose, onAccept, onAcceptTeam, onAcceptColumn, initialMode = 'team', enlight_team, enlight_subteam, enlight_docset_id, canEdit = true }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { divisionOptions } = useDivisionOptions();
  const { teamOptions } = useTeamOptions();
  const { docsetIdList, isLoading, refreshDocsetIdList } = useDocsetIdList();
  const [mode, setMode] = useState<'team' | 'column' | 'mismatch'>(initialMode ?? 'team');
  useEffect(() => { if (open) setMode(initialMode ?? 'team'); }, [initialMode, open]);
  const isTeamMode = mode === 'team';
  const isColumnMode = mode === 'column';
  const isMismatchMode = mode === 'mismatch';

  const divisionLabel = useMemo(() => {
    const d = Number(division);
    if (Number.isNaN(d)) return '';
    const found = (divisionOptions ?? []).find((opt: any) => Number((opt as any).department_id) === d);
    return found?.label ?? '';
  }, [division, divisionOptions]);

  const teamLabel = useMemo(() => {
    const t = Number(team);
    if (Number.isNaN(t)) return '';
    const found = (teamOptions ?? []).find((opt: any) => Number((opt as any).team_id) === t);
    return found?.label ?? '';
  }, [team, teamOptions]);

  const selectedDocset = useMemo(() => {
    const d = Number(division);
    const t = Number(team);
    return (docsetIdList ?? []).find((x: any) => Number(x?.department_id) === d && Number(x?.team_id) === t);
  }, [docsetIdList, division, team]);

  const [enlightTeam, setEnlightTeam] = useState<string>('');
  // 仕様変更: ENLIGHT_DOCSET_LIST は (enlight_team, enlight_subteam) の複合キー。
  // UI上は enlight_team と enlight_subteam を分けず、"チームID サブチーム名" の1つの選択肢として提示する。
  const [enlightSubteam, setEnlightSubteam] = useState<string>('');
  const combinedEnlightKey = (t: string, s: string) => `${t} ${s}`;
  const combinedEnlightOptions = useMemo(() => {
    const list = Array.isArray(docsetIdList) ? docsetIdList : [];
    const seen = new Set<string>();
    const opts: { value: string; label: string; team: string; subteam: string; docsetId: number | null }[] = [];
    list.forEach((x: any) => {
      const t = String(x?.enlight_team ?? '').trim();
      if (!t) return;
      const s = String(x?.enlight_subteam ?? '').trim();
      const key = combinedEnlightKey(t, s);
      if (seen.has(key)) return;
      seen.add(key);
      const docsetIdRaw = x?.docset_id;
      const docsetId = docsetIdRaw !== null && docsetIdRaw !== undefined && Number.isFinite(Number(docsetIdRaw)) ? Number(docsetIdRaw) : null;
      opts.push({ value: key, team: t, subteam: s, docsetId, label: s ? `${t} ${s}` : t });
    });
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [docsetIdList]);

  // 選択中の docset_id。優先度: 1) 親から渡された保存済みの値（enlight_docset_id）、
  // 2) 選択肢一覧から enlight_team + enlight_subteam に対応するものを導出。
  const [enlightDocsetId, setEnlightDocsetId] = useState<number | null>(null);

  // フォールバック補完: 親から docset_id が渡されていない場合のみ、選択肢一覧から導出する
  useEffect(() => {
    if (!open) return;
    if (enlightDocsetId !== null) return;
    const t = (enlightTeam ?? '').trim();
    if (!t) return;
    const s = (enlightSubteam ?? '').trim();
    const found = combinedEnlightOptions.find((o) => o.value === combinedEnlightKey(t, s));
    if (found && found.docsetId !== null) {
      setEnlightDocsetId(found.docsetId);
    }
  }, [open, enlightDocsetId, enlightTeam, enlightSubteam, combinedEnlightOptions]);

  const [detail, setDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // initialize_docset_list 実行中フラグ
  const [initLoading, setInitLoading] = useState<boolean>(false);

  // チーム連携設定 POST 中フラグ
  const [submittingTeam, setSubmittingTeam] = useState<boolean>(false);
  // 「チーム連携を設定する」押下時の確認ダイアログ表示状態
  const [teamConfirmOpen, setTeamConfirmOpen] = useState<boolean>(false);
  // カラム連携設定 POST 中フラグ
  const [submittingColumn, setSubmittingColumn] = useState<boolean>(false);

  // 通知
  const [notifyOpen, setNotifyOpen] = useState<boolean>(false);
  const [notifyMsg, setNotifyMsg] = useState<string>('');

  // /get_team_docset_list の取得結果（カラム紐付けの登録状態）
  const [teamDocsetColumns, setTeamDocsetColumns] = useState<any[]>([]);
  // direct_columns（絶対紐付け定義: enlight_column_name -> column_name）
  const [teamDocsetDirectColumns, setTeamDocsetDirectColumns] = useState<any[]>([]);
  const [columnsLoading, setColumnsLoading] = useState<boolean>(false);
  const [columnsError, setColumnsError] = useState<string | null>(null);

  // 自動初期紐付けの多重実行防止キー
  const autoBindKeyRef = useRef<string | null>(null);
  const nonAbsoluteInitKeyRef = useRef<string | null>(null);
  const userTouchedRef = useRef<boolean>(false);
  // 「チーム切替時クリア」effectが直近に処理した enlightTeam（単なる再オープンでは発火させないための記録）
  const prevEnlightTeamRef = useRef<string | null>(null);

  // 「IDリスト再取得」押下時の処理:
  // 1) initialize_docset_list を POST
  // 2) 正常応答確認後に Docset ID List の再取得をトリガー
  const handleReinitAndRefresh = async () => {
    setInitLoading(true);
    try {
      await apiClient.post('/initialize_docset_list');
      // 正常応答（2xx）の場合のみ再取得
      invalidateDocsetIdListCache();
      refreshDocsetIdList();
    } catch (e) {
      console.error('initialize_docset_list error', e);
    } finally {
      setInitLoading(false);
    }
  };

  // detail から fields を抽出
  const fields = useMemo(() => {
    const arr = detail?.json_data?.schema?.fields;
    return Array.isArray(arr) ? arr : [];
  }, [detail]);

  // 紐付け選択状態: field.name -> 'common:xxx' | 'free:yyy' | ''
  const [bindingMap, setBindingMap] = useState<Record<string, string>>({});
  useEffect(() => {
    // フィールド変更や課・チーム変更時にキーを整える（既存選択は可能な限り保持）
    setBindingMap((prev) => {
      const next: Record<string, string> = {};
      fields.forEach((f: any) => {
        const key = String(f?.name ?? '');
        if (!key) return;
        next[key] = prev[key] ?? '';
      });
      return next;
    });
  }, [fields, division, team]);

  const handleBindChange = (fieldName: string) => (e: any) => {
    const v = String(e.target.value ?? '');
    userTouchedRef.current = true;
    setBindingMap((prev) => ({ ...prev, [fieldName]: v }));
  };

  // 右側リスト用：共通項目名／フリー項目名の取得
  const { reportTypeOptions } = useReportTypeOptions({ department_id: division, team_id: team });
  const { projectOptions } = useProjectOptions({ department_id: division, team_id: team });
  const { powerTrainOptions } = usePowerTrainOptions({ department_id: division, team_id: team });
  const { gradeOptions } = useGradeOptions({ department_id: division, team_id: team });
  const { shimukeOptions } = useShimukeOptions({ department_id: division, team_id: team });
  const { lotOptions } = useLotOptions({ department_id: division, team_id: team });
  const { freeOptions: freeOptionsFetched } = useFreeOptions({ department_id: division, team_id: team });


  // Enlight 側 dtype を Body 種別へ正規化
  const mapEnlightDtypeToBodyType = (dtype: any): 'number' | 'date' | 'list' | 'text' => {
    const d = String(dtype ?? '').toLowerCase();
    if (d.includes('date')) return 'date';
    if (d === 'number' || d.includes('int') || d.includes('num') || d.includes('float')) return 'number';
    if (d.includes('list') || d.includes('array') || d.includes('select')) return 'list';
    return 'text';
  };

  const parseBinding = (v: string) => {
    if (typeof v !== 'string') return { kind: null as null, name: '' };
    if (v.startsWith('common:')) return { kind: 'common' as const, name: v.slice(7) };
    if (v.startsWith('free:')) return { kind: 'free' as const, name: v.slice(5) };
    if (v.startsWith('basic:')) return { kind: 'basic' as const, name: v.slice(6) };
    return { kind: null as null, name: '' };
  };

  // 既に他行で選択済みの項目名を把握（右テーブル非表示用）
  const selectedCommonNamesSet = useMemo(() => {
    const s = new Set<string>();
    Object.values(bindingMap ?? {}).forEach((v) => {
      const p = parseBinding(String(v));
      if (p.kind === 'common' && p.name) s.add(p.name);
    });
    return s;
  }, [bindingMap]);

  const selectedFreeNamesSet = useMemo(() => {
    const s = new Set<string>();
    Object.values(bindingMap ?? {}).forEach((v) => {
      const p = parseBinding(String(v));
      if (p.kind === 'free' && p.name) s.add(p.name);
    });
    return s;
  }, [bindingMap]);




  // 共通項目: 表示名と種別
  const commonItems = useMemo(() => {
    const metas = [
      reportTypeOptions?.[0],
      projectOptions?.[0],
      powerTrainOptions?.[0],
      gradeOptions?.[0],
      shimukeOptions?.[0],
      lotOptions?.[0],
    ].filter(Boolean) as any[];
    const map = new Map<string, { name: string; type: string }>();
    metas.forEach((m: any) => {
      const name = String(m?.label ?? '').trim();
      if (!name) return;
      if (!map.has(name)) {
        map.set(name, { name, type: computeBodyType(m?.attr_type, m?.multiple_select) });
      }
    });
    return Array.from(map.values());
  }, [reportTypeOptions, projectOptions, powerTrainOptions, gradeOptions, shimukeOptions, lotOptions]);

  // フリー項目: 表示名と種別（課・チームでフィルタ）
  const freeItems = useMemo(() => {
    const d = Number(division);
    const t = Number(team);
    const arr = (freeOptionsFetched ?? [])
      .filter((state: any) => Number(state?.department_id) === d && Number(state?.team_id) === t);
    const map = new Map<string, { name: string; type: string }>();
    arr.forEach((m: any) => {
      const name = String(m?.label ?? '').trim();
      if (!name) return;
      if (!map.has(name)) {
        map.set(name, { name, type: computeBodyType(m?.attr_type, m?.multiple_select) });
      }
    });
    return Array.from(map.values());
  }, [freeOptionsFetched, division, team]);

  // attr_id -> 共通項目ラベル
  const commonNameByAttrId = useMemo(() => {
    const map = new Map<number, string>();
    const metas = [
      reportTypeOptions?.[0],
      projectOptions?.[0],
      powerTrainOptions?.[0],
      gradeOptions?.[0],
      shimukeOptions?.[0],
      lotOptions?.[0],
    ].filter(Boolean) as any[];
    metas.forEach((m: any) => {
      const id = Number(m?.attr_id);
      const name = String(m?.label ?? '').trim();
      if (Number.isFinite(id) && name) map.set(id, name);
    });
    return map;
  }, [reportTypeOptions, projectOptions, powerTrainOptions, gradeOptions, shimukeOptions, lotOptions]);

  // attr_id -> フリー項目ラベル（課・チームでフィルタ）
  const freeNameByAttrId = useMemo(() => {
    const map = new Map<number, string>();
    const d = Number(division);
    const t = Number(team);
    const arr = (freeOptionsFetched ?? []).filter(
      (state: any) => Number(state?.department_id) === d && Number(state?.team_id) === t
    );
    arr.forEach((m: any) => {
      const id = Number(m?.attr_id);
      const name = String(m?.label ?? '').trim();
      if (Number.isFinite(id) && name) map.set(id, name);
    });
    return map;
  }, [freeOptionsFetched, division, team]);

  // 逆引き: 共通/フリー 項目名 -> attr_id
  const commonAttrIdByName = useMemo(() => {
    const m = new Map<string, number>();
    commonNameByAttrId.forEach((name, id) => {
      if (name) m.set(name, id);
    });
    return m;
  }, [commonNameByAttrId]);

  const freeAttrIdByName = useMemo(() => {
    const m = new Map<string, number>();
    freeNameByAttrId.forEach((name, id) => {
      if (name) m.set(name, id);
    });
    return m;
  }, [freeNameByAttrId]);
 
  // 右テーブルに表示する（未紐付けの）項目のみ
  const visibleCommonItems = useMemo(
    () => commonItems.filter((it) => !selectedCommonNamesSet.has(it.name)),
    [commonItems, selectedCommonNamesSet]
  );
  const visibleFreeItems = useMemo(
    () => freeItems.filter((it) => !selectedFreeNamesSet.has(it.name)),
    [freeItems, selectedFreeNamesSet]
  );

  // ENLIGHT_TEAM_COLUMN_ABSOLUTE_LIST マッピング
  // 優先: /get_team_docset_list の direct_columns（フォールバック: 定数）
  const absoluteMap = useMemo(() => {
    const m = new Map<string, string>();
    const arr = Array.isArray(teamDocsetDirectColumns) ? teamDocsetDirectColumns : [];
    if (arr.length > 0) {
      arr.forEach((rec: any) => {
        if (rec?.enlight_column_name_mismatch === true) return;
        const k = String(rec?.enlight_column_name ?? '').trim();
        const v = String(rec?.column_name ?? '').trim();
        if (k && v) m.set(k, v);
      });
    }
    if (m.size === 0) {
      (ENLIGHT_TEAM_COLUMN_ABSOLUTE_LIST ?? []).forEach((obj: any) => {
        const entries = Object.entries(obj ?? {});
        if (entries.length > 0) {
          const [k, v] = entries[0];
          if (k && v != null) m.set(String(k), String(v));
        }
      });
    }
    return m;
  }, [teamDocsetDirectColumns]);

  const nonAbsoluteDirectMap = useMemo(() => {
    const m = new Map<string, string>();
    const arr = Array.isArray(teamDocsetDirectColumns) ? teamDocsetDirectColumns : [];
    arr.forEach((rec: any) => {
      if (rec?.enlight_column_name_mismatch !== true) return;
      const k = String(rec?.enlight_column_name ?? '').trim();
      const v = String(rec?.column_name ?? '').trim();
      if (k && v) m.set(k, v);
    });
    return m;
  }, [teamDocsetDirectColumns]);

  const nonAbsoluteDirectKey = useMemo(() => {
    if (nonAbsoluteDirectMap.size === 0) return '';
    return Array.from(nonAbsoluteDirectMap.entries())
      .map(([k, v]) => `${k}=>${v}`)
      .sort()
      .join('|');
  }, [nonAbsoluteDirectMap]);

  // 絶対紐付け行は常に最新の absoluteMap の値へ同期する（ユーザーは編集不可のため上書きしても問題ない）。
  // teamDocsetDirectColumns 取得完了前は ENLIGHT_TEAM_COLUMN_ABSOLUTE_LIST のフォールバック値
  // （例: APPROVER: '職制' など、実データと一致しない場合がある）が一時的に入るため、
  // 「未設定のときのみ反映」にすると、取得完了後に正しい値へ補正されずに残ってしまう。
  // useLayoutEffect: ペイント前に同期反映し、「未設定」の一瞬のちらつきを防ぐ
  useLayoutEffect(() => {
    if (!Array.isArray(fields) || fields.length === 0) return;
    setBindingMap((prev) => {
      const next: Record<string, string> = { ...prev };
      let changed = false;
      fields.forEach((f: any) => {
        const fname = String(f?.name ?? '').trim();
        if (!fname) return;
        const abs = absoluteMap.get(fname);
        if (abs) {
          const val = `basic:${abs}`;
          if (next[fname] !== val) {
            next[fname] = val;
            changed = true;
          }
        }
      });
      return changed ? next : prev;
    });
  }, [fields, absoluteMap]);

  // direct_columns 由来（mismatch=true）は固定しないが、未設定時の初期値として反映する
  // useLayoutEffect: ペイント前に同期反映し、「未設定」の一瞬のちらつきを防ぐ
  useLayoutEffect(() => {
    if (!open || !isColumnMode) return;
    if (!Array.isArray(fields) || fields.length === 0) return;
    if (nonAbsoluteDirectMap.size === 0) return;

    const key = `${division}:${team}:${enlightTeam}:${fields.length}:${nonAbsoluteDirectKey}`;
    if (nonAbsoluteInitKeyRef.current === key) return;
    nonAbsoluteInitKeyRef.current = key;

    setBindingMap((prev) => {
      const next: Record<string, string> = { ...prev };
      let changed = false;
      fields.forEach((f: any) => {
        const fname = String(f?.name ?? '').trim();
        if (!fname) return;
        const mapped = nonAbsoluteDirectMap.get(fname);
        if (!mapped) return;
        if (next[fname]) return;
        next[fname] = `basic:${mapped}`;
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [open, isColumnMode, fields, nonAbsoluteDirectMap, nonAbsoluteDirectKey, division, team, enlightTeam]);

  // Enlightチーム切替時に、前回チームの手動/自動選択が残らないよう、絶対紐付け以外をクリア
  // 注意: 単にダイアログを開き直しただけ（enlightTeamの値自体は不変）では発火させない。
  // ここで毎回クリアすると、サーバー保存済みの共通/フリー項目の紐付けが復元されないまま消えてしまう。
  // useLayoutEffect: ペイント前に同期反映し、切替直後の一瞬のちらつきを防ぐ
  useLayoutEffect(() => {
    if (!open || !isColumnMode) return;

    if (prevEnlightTeamRef.current === enlightTeam) return;
    prevEnlightTeamRef.current = enlightTeam;

    setBindingMap(() => {
      const next: Record<string, string> = {};
      (fields ?? []).forEach((f: any) => {
        const fname = String(f?.name ?? '').trim();
        if (!fname) return;
        const abs = absoluteMap.get(fname);
        const nonAbs = nonAbsoluteDirectMap.get(fname);
        // 絶対紐付けは既定値を入れる／それ以外は空
        next[fname] = abs ? `basic:${abs}` : (nonAbs ? `basic:${nonAbs}` : '');
      });
      return next;
    });

    // 自動割当の多重実行キーをリセット（新チームで再評価させる）
    autoBindKeyRef.current = null;
    nonAbsoluteInitKeyRef.current = null;
    userTouchedRef.current = false;
  }, [enlightTeam, fields, absoluteMap, nonAbsoluteDirectMap, open, isColumnMode]);
  
  // 基本項目（direct_columns 準拠）: カラム名=column_name, 種別=enlight_dtype
  const basicItems = useMemo(() => {
    const arr = Array.isArray(teamDocsetDirectColumns) ? teamDocsetDirectColumns : [];
    return arr
      .map((rec: any) => ({
        name: String(rec?.column_name ?? '').trim(),
        // API の表記揺れに対応（enlight_dtype / enlight_dype）
        type: String((rec as any)?.enlight_dtype ?? (rec as any)?.enlight_dype ?? '').trim(),
      }))
      .filter((it) => it.name);
  }, [teamDocsetDirectColumns]);

  // 左テーブルで使用されている週報カラム名の集合（プルダウン選択状態に基づく）
  const usedWeeklyColumnNames = useMemo(() => {
    const set = new Set<string>();
    Object.values(bindingMap ?? {}).forEach((v: any) => {
      const p = parseBinding(String(v));
      if (p.name) set.add(p.name);
    });
    return set;
  }, [bindingMap]);

  // 表示用（基本項目）: 左テーブルで未使用の direct_columns のみ
  const filteredBasicItems = useMemo(() => {
    const arr = Array.isArray(basicItems) ? basicItems : [];
    if (!usedWeeklyColumnNames || usedWeeklyColumnNames.size === 0) return [];
    return arr.filter((it: any) => !usedWeeklyColumnNames.has(it.name));
  }, [basicItems, usedWeeklyColumnNames]);

  useEffect(() => {
    if (open) {
      const normalize = (v: any) => String(v ?? '').trim();
      const preferred = normalize(enlight_team);
      const fallback = normalize(selectedDocset?.enlight_team);
      setEnlightTeam(preferred || fallback || '');
      setEnlightSubteam(normalize(enlight_subteam) || '');
      // docset_id: 親から渡された保存済みの値を優先採用（無ければ後続のフォールバックeffectで選択肢から導出）
      const propDocsetId = Number(enlight_docset_id);
      setEnlightDocsetId(Number.isFinite(propDocsetId) ? propDocsetId : null);
    }
  }, [open, selectedDocset, enlight_team, enlight_subteam, enlight_docset_id]);

  useEffect(() => {
    // 未連携（空）の場合は詳細表示をクリア
    if (!enlightTeam) {
      setDetail(null);
      setDetailError(null);
      setDetailLoading(false);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setDetailLoading(true);
      setDetailError(null);
      try {
        const res = await apiClient.get('/get_docset_id_detail', {
          params: {
            enlight_team: enlightTeam,
            ...(enlightSubteam ? { enlight_subteam: enlightSubteam } : {}),
          },
        });
        if (cancelled) return;
        setDetail(res?.data ?? null);
      } catch (e: any) {
        if (!cancelled) setDetailError('詳細の取得に失敗しました');
        console.error('get_docset_id_detail error', e);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [enlightTeam, enlightSubteam]);

  // カラム設定モード時に /get_team_docset_list を取得し、登録状態（columns/direct_columns）を保持
  useEffect(() => {
    if (!open || !isColumnMode) {
      setTeamDocsetColumns([]);
      setTeamDocsetDirectColumns([]);
      setColumnsError(null);
      setColumnsLoading(false);
      return;
    }
    // 未連携のときは初期化
    if (!enlightTeam) {
      setTeamDocsetColumns([]);
      setTeamDocsetDirectColumns([]);
      setColumnsError(null);
      setColumnsLoading(false);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setColumnsLoading(true);
      setColumnsError(null);
      try {
        const res = await apiClient.get('/get_team_docset_list', {
          params: {
            department_id: Number(division) || 0,
            team_id: Number(team) || 0,
          },
        });
        if (cancelled) return;
        const data: any = res?.data;

        // columns を配列化
        const cols: any = data?.columns;
        const colsArray: any[] = Array.isArray(cols)
          ? cols
          : (cols && typeof cols === 'object')
            ? Object.values(cols)
            : [];
        setTeamDocsetColumns(colsArray);

        // direct_columns を配列化（優先的に使用）
        const dcols: any = data?.direct_columns;
        const dcolsArray: any[] = Array.isArray(dcols)
          ? dcols
          : (dcols && typeof dcols === 'object')
            ? Object.values(dcols)
            : [];
        setTeamDocsetDirectColumns(dcolsArray);
      } catch (e) {
        if (!cancelled) {
          setTeamDocsetColumns([]);
          setTeamDocsetDirectColumns([]);
          setColumnsError('取得に失敗しました');
        }
      } finally {
        if (!cancelled) setColumnsLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [open, isColumnMode, division, team, enlightTeam]);

  // 取得した登録状態（columns）を左テーブルのプルダウン初期選択に反映
  // 注意:
  // - MUI Select の "out-of-range" を避けるため、プルダウン候補に存在する値のみを自動設定する
  //   ・左の型(leftType)と一致するか
  //   ・既に他行で使用済みでないか（重複不可）
  // - 絶対紐付け行は対象外
  useEffect(() => {
    if (!open || !isColumnMode) return;
    if (!enlightTeam) return;
    if (!Array.isArray(fields) || fields.length === 0) return;
    if (!Array.isArray(teamDocsetColumns) || teamDocsetColumns.length === 0) return;
    // 共通/フリー候補が未ロードなら自動割当を遅延
    if ((commonItems?.length ?? 0) + (freeItems?.length ?? 0) === 0) return;
    if (userTouchedRef.current) return;

    // 絶対紐付け（固定表示）は対象外（direct_columns 優先、無ければ定数フォールバック）
    const absoluteKeys = new Set<string>();
    if (Array.isArray(teamDocsetDirectColumns) && teamDocsetDirectColumns.length > 0) {
      teamDocsetDirectColumns.forEach((rec: any) => {
        if (rec?.enlight_column_name_mismatch === true) return;
        const k = String(rec?.enlight_column_name ?? '').trim();
        if (k) absoluteKeys.add(k);
      });
    } else {
      (ENLIGHT_TEAM_COLUMN_ABSOLUTE_LIST ?? []).forEach((obj: any) => {
        const keys = Object.keys(obj ?? {});
        if (keys.length > 0) absoluteKeys.add(String(keys[0]));
      });
    }

    // すでに何かしら選択済みなら自動割当は行わない（無限更新回避）
    // → 絶対紐付け（absoluteKeys）と、direct_columns由来の非絶対デフォルト値（nonAbsoluteDirectMap）の
    //   初期値は「ユーザーの選択」ではないため除外し、それ以外の選択がある場合のみ停止
    const hasAnySelection = Object.entries(bindingMap ?? {}).some(
      ([k, v]) => !!v && !absoluteKeys.has(String(k)) && !nonAbsoluteDirectMap.has(String(k))
    );
    if (hasAnySelection) return;

    // 同一キーでの多重実行防止
    const key = `${division}:${team}:${enlightTeam}:${fields.length}:${teamDocsetColumns.length}:${commonItems.length}:${freeItems.length}`;
    if (autoBindKeyRef.current === key) return;

    let changedOuter = false;
    setBindingMap((prev) => {
      const next: Record<string, string> = { ...prev };

      // 既に選択済みの名称を収集（重複回避）
      const usedCommon = new Set<string>();
      const usedFree = new Set<string>();
      Object.values(next).forEach((v) => {
        const p = parseBinding(String(v));
        if (p.kind === 'common' && p.name) usedCommon.add(p.name);
        if (p.kind === 'free' && p.name) usedFree.add(p.name);
      });

      let changed = false;

      // フィールドごとに自動割当
      fields.forEach((f: any) => {
        const fname = String(f?.name ?? '').trim();
        if (!fname || absoluteKeys.has(fname)) return;

        // 既にユーザー操作／前段で設定済みなら上書きしない
        if (next[fname]) return;

        // /get_team_docset_list.columns から同名カラムを探す
        const rec = (teamDocsetColumns as any[]).find(
          (c: any) => String(c?.enlight_column_name ?? '').trim() === fname
        );
        if (!rec) return;

        const attrId = Number((rec as any)?.attr_id);
        if (!Number.isFinite(attrId)) return;

        const leftType = mapEnlightDtypeToBodyType(f?.enlight_dtype);

        // 共通 -> フリー の順で試す（いずれも型一致かつ未使用のときのみ採用）
        if (commonNameByAttrId.has(attrId)) {
          const nm = commonNameByAttrId.get(attrId)!;
          const candidate = (commonItems ?? []).find((it) => it.name === nm);
          if (candidate && !usedCommon.has(nm)) {
            next[fname] = `common:${nm}`;
            usedCommon.add(nm);
            changed = true;
            return;
          }
        }

        if (freeNameByAttrId.has(attrId)) {
          const nm = freeNameByAttrId.get(attrId)!;
          const candidate = (freeItems ?? []).find((it) => it.name === nm);
          if (candidate && !usedFree.has(nm)) {
            next[fname] = `free:${nm}`;
            usedFree.add(nm);
            changed = true;
            return;
          }
        }
        // 条件に合わなければ未設定のまま（プルダウン候補外の値は入れない）
      });

      changedOuter = changed;
      return changed ? next : prev;
    });

    // 実際に割当が発生した場合のみキーをマーク（候補未整備時の早期固定を避ける）
    if (changedOuter) {
      autoBindKeyRef.current = key;
    }
  }, [open, isColumnMode, enlightTeam, fields, teamDocsetColumns, teamDocsetDirectColumns, nonAbsoluteDirectMap, commonNameByAttrId, freeNameByAttrId, commonItems, freeItems, bindingMap, division, team]);


  const handleAcceptTeam = async () => {
    const subteamTrimmed = (enlightSubteam ?? '').trim();
    const payload = {
      department_id: Number(division) || 0,
      team_id: Number(team) || 0,
      enlight_team: (enlightTeam ?? '').trim(),
      enlight_subteam: subteamTrimmed || null,
      docset_id: enlightDocsetId,
    };
    setSubmittingTeam(true);
    try {
      await apiClient.post('/upsert_team_docset', payload);
      // 親へ通知（状態更新など）
      if (onAcceptTeam) {
        onAcceptTeam({ division, team, enlight_team: payload.enlight_team, enlight_subteam: payload.enlight_subteam, docset_id: payload.docset_id });
      } else if (onAccept) {
        // 後方互換
        onAccept({ division, team, enlight_team: payload.enlight_team, enlight_subteam: payload.enlight_subteam, docset_id: payload.docset_id });
      }
      // インフォメーション表示
      setNotifyMsg('チーム連携設定が完了しました。続いてEnlightカラムの連携設定を定義してください。');
      setNotifyOpen(true);
      // カラム設定モードへ遷移
      setMode('column');
    } catch (e) {
      console.error('upsert_team_docset error', e);
      setNotifyMsg('チーム連携設定に失敗しました。しばらくしてから再度お試しください。');
      setNotifyOpen(true);
    } finally {
      setSubmittingTeam(false);
    }
  };

  const handleAcceptColumn = async () => {
    const teamStr = (enlightTeam ?? '').trim();
    if (!teamStr) {
      setNotifyMsg('EnlightチームIDが未選択のため、カラム連携を送信できません。');
      setNotifyOpen(true);
      return;
    }

    // 左テーブルのフィールド情報を name -> field に変換
    const fieldMap = new Map<string, any>();
    (fields ?? []).forEach((f: any) => {
      const n = String(f?.name ?? '').trim();
      if (n) fieldMap.set(n, f);
    });

    // columns を構築（プルダウンがない行や未選択は送信しない）
    const columns: any[] = [];
    Object.entries(bindingMap ?? {}).forEach(([fname, v]) => {
      const name = String(fname ?? '').trim();
      if (!name) return;

      const parsed = parseBinding(String(v ?? ''));
      if (!parsed.kind || !parsed.name) return;

      // すべての行を送信対象にする（absolute も含む）

      // attr_id を決定（basic は NULL）
      let attrId: number | null = null;
      if (parsed.kind === 'common') {
        const id = commonAttrIdByName.get(parsed.name);
        if (!Number.isFinite(id as number)) return;
        attrId = Number(id);
      } else if (parsed.kind === 'free') {
        const id = freeAttrIdByName.get(parsed.name);
        if (!Number.isFinite(id as number)) return;
        attrId = Number(id);
      } else if (parsed.kind === 'basic') {
        attrId = null;
      }

      const f = fieldMap.get(name);
      const dtype = String(f?.enlight_dtype ?? '');

      // column_direct_name は「基本:」選択時にユーザーが選んだ週報カラム名そのもの
      // （absoluteMap に値が存在するかどうかとは無関係。ここで絞り込むと、絶対紐付き
      //   以外の basic 選択（例: REGISTER/TEAM/CONTENTS）が null 送信され、保存済みの
      //   個別設定が消えてしまう）
      const directName: string | null = parsed.kind === 'basic' ? parsed.name : null;

      columns.push({
        attr_id: attrId,
        enlight_column_name: name,
        enlight_dtype: dtype,
        column_direct_name: directName,
      });
    });

    const subteamTrimmed = (enlightSubteam ?? '').trim();
    const payload = {
      department_id: Number(division) || 0,
      team_id: Number(team) || 0,
      enlight_team: teamStr,
      enlight_subteam: subteamTrimmed || null,
      docset_id: enlightDocsetId,
      reset_flg: 1,
      columns,
    };

    setSubmittingColumn(true);
    try {
      await apiClient.post('/upsert_team_docset_columns', payload);

      // 成功通知
      setNotifyMsg('カラム連携設定が完了しました。');
      setNotifyOpen(true);

      // 親へ通知（互換のため bindings を渡す）
      if (onAcceptColumn) {
        onAcceptColumn({
          division,
          team,
          enlight_team: teamStr,
          enlight_subteam: subteamTrimmed || null,
          bindings: { ...bindingMap },
        });
      }
      onClose();
    } catch (e) {
      console.error('upsert_team_docset_columns error', e);
      setNotifyMsg('カラム連携設定に失敗しました。しばらくしてから再度お試しください。');
      setNotifyOpen(true);
    } finally {
      setSubmittingColumn(false);
    }
  };

  const acceptButtonLabel = isTeamMode ? 'チーム連携を設定する' : 'カラム連携を設定する';

  return (
    <Dialog
      open={open}
      maxWidth={false}
      fullScreen={fullScreen}
      fullWidth
      PaperProps={{ sx: { width: { xs: '90vw', sm: '85vw' }, maxWidth: 1600 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <span>Enlight TeamID 連携設定</span>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant={isMismatchMode ? 'contained' : 'outlined'}
            onClick={() => setMode(isMismatchMode ? 'team' : 'mismatch')}
            disabled={!canEdit || !enlightTeam}
          >
            週報連携確認
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ReplayIcon />}
            onClick={handleReinitAndRefresh}
            disabled={!canEdit || isLoading || initLoading || submittingTeam}
          >
            IDリスト再取得
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Card sx={{ backgroundColor: isDark ? '#1e293b' : '#f0f0f0' }}>
            <CardContent>
              <Box sx={{ display: 'none', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
                <Button size="small" variant={isTeamMode ? 'contained' : 'outlined'} onClick={() => setMode('team')}>チーム設定</Button>
                <Button size="small" variant={isColumnMode ? 'contained' : 'outlined'} onClick={() => setMode('column')}>カラム設定</Button>
              </Box>
              <Grid container spacing={2} className="search-row">
                <Grid size={4} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                  <InputLabel
                    htmlFor="division-display"
                    sx={{ minHeight: 32, display: 'flex', alignItems: 'center', mb: 1 }}
                  >
                    課
                  </InputLabel>
                  <TextField
                    id="division-display"
                    value={divisionLabel}
                    sx={{ backgroundColor: isDark ? '#1e293b' : 'white', minHeight: 40, '.MuiInputBase-input': { height: 40, boxSizing: 'border-box', padding: '8px 14px' } }}
                    fullWidth
                    disabled
                  />
                </Grid>

                <Grid size={4} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                  <InputLabel
                    htmlFor="team-display"
                    sx={{ minHeight: 32, display: 'flex', alignItems: 'center', mb: 1 }}
                  >
                    チーム
                  </InputLabel>
                  <TextField
                    id="team-display"
                    value={teamLabel}
                    sx={{ backgroundColor: isDark ? '#1e293b' : 'white', minHeight: 40, '.MuiInputBase-input': { height: 40, boxSizing: 'border-box', padding: '8px 14px' } }}
                    fullWidth
                    disabled
                  />
                </Grid>

                <Grid size={4} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                  <InputLabel
                    htmlFor="enlight-team"
                    sx={{ minHeight: 32, display: 'flex', alignItems: 'center', mb: 1 }}
                  >
                    EnlightチームID
                  </InputLabel>
                  <FormControl fullWidth>
                    <CommonSelect
                      id="enlight-team"
                      options={combinedEnlightOptions.map((o) => ({ value: o.value, label: o.label }))}
                      value={(() => {
                        const t = (enlightTeam ?? '').trim();
                        if (!t) return null;
                        const s = (enlightSubteam ?? '').trim();
                        const key = combinedEnlightKey(t, s);
                        const found = combinedEnlightOptions.find((o) => o.value === key);
                        return found ? { value: found.value, label: found.label } : { value: key, label: s ? `${t} ${s}` : t };
                      })()}
                      onChange={(opt) => {
                        const found = combinedEnlightOptions.find((o) => o.value === opt?.value);
                        setEnlightTeam(found?.team ?? '');
                        setEnlightSubteam(found?.subteam ?? '');
                        setEnlightDocsetId(found?.docsetId ?? null);
                      }}
                      placeholder="未連携"
                      isDisabled={!canEdit || isLoading || isColumnMode || submittingTeam}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              {isColumnMode && (<Box sx={{ mt: 2 }}>
                <InputLabel sx={{ mb: 1, fontWeight: 'bold' }}>Enlightカラムとの対応設定</InputLabel>
                {enlightTeam === '' ? (
                  <Box sx={{ fontSize: 14, color: 'text.secondary' }}>「未連携」のため、設定できません。</Box>
                ) : detailLoading || columnsLoading ? (
                  <Box sx={{ fontSize: 14, color: 'text.secondary' }}>読込中...</Box>
                ) : detailError ? (
                  <Box sx={{ fontSize: 14, color: 'error.main' }}>{detailError}</Box>
                ) : detail ? (
                  <Grid container spacing={2} columns={10}>
                    <Grid size={6}>
                      <Card variant="outlined">
                        <CardContent sx={{ p: 0 }}>
                          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle1" fontWeight="bold">Enlightカラム</Typography>
                          </Box>
                          <TableContainer component={Paper} elevation={0} sx={{ height: 320, overflow: 'auto' }} className={canEdit ? undefined : 'read-only-table'}>
                            <Table stickyHeader size="small" aria-label="Enlightカラム" sx={{ tableLayout: 'fixed' }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ width: '35%' }}>カラム名</TableCell>
                                  <TableCell sx={{ width: '20%' }}>種別</TableCell>
                                  <TableCell sx={{ width: '10%' }}>説明</TableCell>
                                  <TableCell sx={{ width: '35%' }}>対応する週報カラム</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {fields.length > 0 ? (
                                  fields.map((f: any, idx: number) => {
                                    const fname = String(f?.name ?? '');
                                    const value = bindingMap[fname] ?? '';

                                    // 左の Enlight フィールド種別 → 右の Body 種別に合わせる
                                    const leftType = mapEnlightDtypeToBodyType(f?.enlight_dtype);

                                    // 絶対マッピング（固定表示）判定
                                    const absoluteLabel = absoluteMap.get(fname);
 
                                    // 他の行で既に選択済の項目はプルダウンから除外（ただし自分が選択中のものは残す）
                                    const parsed = parseBinding(value);
                                    const selectedCommonByOthers = new Set(selectedCommonNamesSet);
                                    const selectedFreeByOthers = new Set(selectedFreeNamesSet);
                                    if (parsed.kind === 'common' && parsed.name) selectedCommonByOthers.delete(parsed.name);
                                    if (parsed.kind === 'free' && parsed.name) selectedFreeByOthers.delete(parsed.name);

                                    const usedBasicByOthers = new Set<string>(usedWeeklyColumnNames);
                                    if (parsed.kind === 'basic' && parsed.name) usedBasicByOthers.delete(parsed.name);
                                    const availableBasic = basicItems
                                      .filter((it: any) => !usedBasicByOthers.has(it.name));

                                    const availableCommon = commonItems
                                      .filter((it) => !selectedCommonByOthers.has(it.name));
                                    const availableFree = freeItems
                                      .filter((it) => !selectedFreeByOthers.has(it.name));

                                    const highlighted = Boolean(absoluteLabel || (value && value !== ''));

                                    return (
                                      <TableRow key={`${fname}-${idx}`} hover sx={{ bgcolor: highlighted ? '#d9fad2' : undefined, ...(highlighted && isDark ? { '& .MuiTableCell-root': { backgroundColor: '#14532d !important' } } : {}) }}>
                                        <TableCell sx={{ whiteSpace: 'nowrap', width: '35%' }}>{fname}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap', width: '20%' }}>{String(f?.enlight_dtype ?? '')}</TableCell>
                                        <TableCell sx={{ width: '10%' }}>
                                          <Tooltip title={String(f?.description ?? '')} arrow enterDelay={300}>
                                            <Link component="button" onClick={(e: any) => e.preventDefault()} underline="hover" sx={{ cursor: 'help' }}>
                                              説明
                                            </Link>
                                          </Tooltip>
                                        </TableCell>
                                        <TableCell align="right" sx={{ minWidth: 220, width: '35%' }}>
                                          <FormControl fullWidth>
                                            <Tooltip
                                              title="Enlight連携が自動で設定される項目は変更できません"
                                              arrow
                                              enterDelay={300}
                                              disableHoverListener={!absoluteLabel}
                                              disableFocusListener={!absoluteLabel}
                                              disableTouchListener={!absoluteLabel}
                                              disableInteractive
                                            >
                                              <span>
                                                <CommonSelect
                                                  id={`bind-${idx}`}
                                                  isDisabled={!canEdit || Boolean(absoluteLabel)}
                                                  options={[
                                                    // 既存の基本（未使用のみ）
                                                    ...availableBasic.map((it) => ({ value: `basic:${it.name}`, label: `基本: ${it.name}` })),
                                                    // 絶対ラベルが basicItems にない場合でも、この行用に選択肢へ注入（他行で未使用 or 自分が選択中のとき）
                                                    ...(absoluteLabel &&
                                                      (!usedBasicByOthers.has(absoluteLabel) ||
                                                        (parsed.kind === 'basic' && parsed.name === absoluteLabel)) &&
                                                      !availableBasic.some((it) => it.name === absoluteLabel)
                                                      ? [{ value: `basic:${absoluteLabel}`, label: `基本: ${absoluteLabel}` }]
                                                      : []),
                                                    // 共通/フリー
                                                    ...availableCommon.map((it) => ({ value: `common:${it.name}`, label: `共通: ${it.name}` })),
                                                    ...availableFree.map((it) => ({ value: `free:${it.name}`, label: `フリー: ${it.name}` })),
                                                  ]}
                                                  value={(() => {
                                                    const opts = [
                                                      // 既存の基本（未使用のみ）
                                                      ...availableBasic.map((it) => ({ value: `basic:${it.name}`, label: `基本: ${it.name}` })),
                                                      // 自身の選択値（basic）を保持する注入は、絶対紐付けの場合のみ許可（前回選択の持ち越し防止）
                                                      ...(parsed.kind === 'basic' &&
                                                        parsed.name &&
                                                        parsed.name === absoluteLabel &&
                                                        !availableBasic.some((it) => it.name === parsed.name)
                                                        ? [{ value: `basic:${parsed.name}`, label: `基本: ${parsed.name}` }]
                                                        : []),
                                                      // 絶対ラベルが basicItems にない場合でも候補に含める（自分が選択中でなくても、options 側で表示できるように）
                                                      ...(absoluteLabel &&
                                                        (!usedBasicByOthers.has(absoluteLabel) ||
                                                          (parsed.kind === 'basic' && parsed.name === absoluteLabel)) &&
                                                        !availableBasic.some((it) => it.name === absoluteLabel)
                                                        ? [{ value: `basic:${absoluteLabel}`, label: `基本: ${absoluteLabel}` }]
                                                        : []),
                                                      // 共通/フリー
                                                      ...availableCommon.map((it) => ({ value: `common:${it.name}`, label: `共通: ${it.name}` })),
                                                      ...availableFree.map((it) => ({ value: `free:${it.name}`, label: `フリー: ${it.name}` })),
                                                    ];
                                                    return opts.find((o) => o.value === value) || null;
                                                  })()}
                                                  onChange={(opt) => handleBindChange(fname)({ target: { value: opt?.value ?? '' } } as any)}
                                                  placeholder="未設定"
                                                />
                                              </span>
                                            </Tooltip>
                                          </FormControl>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })
                                ) : (
                                  <TableRow>
                                    <TableCell colSpan={4} sx={{ color: 'text.secondary' }}>データはありません。</TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid size={4}>
                      <Card variant="outlined">
                        <CardContent sx={{ p: 0 }}>
                          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle1" fontWeight="bold">未使用の週報カラム</Typography>
                          </Box>
                          <TableContainer component={Paper} elevation={0} sx={{ height: 320, overflow: 'auto' }} className={canEdit ? undefined : 'read-only-table'}>
                            <Table stickyHeader size="small" aria-label="未使用の週報カラム" sx={{ tableLayout: 'fixed' }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ width: '20%' }}></TableCell>
                                  <TableCell sx={{ width: '60%' }}>カラム名</TableCell>
                                  <TableCell sx={{ width: '20%' }}>種別</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {filteredBasicItems.length > 0 || visibleCommonItems.length > 0 || visibleFreeItems.length > 0 ? (
                                  <>
                                    {filteredBasicItems.map((it: { name: string; type: string }, idx: number) => (
                                      <TableRow key={`basic-${it.name}-${idx}`}>
                                        <TableCell>基本</TableCell>
                                        <TableCell>{it.name}</TableCell>
                                        <TableCell>{it.type}</TableCell>
                                      </TableRow>
                                    ))}
                                    {visibleCommonItems.map((it: { name: string; type: string }, idx: number) => (
                                      <TableRow key={`common-${it.name}-${idx}`}>
                                        <TableCell>共通</TableCell>
                                        <TableCell>{it.name}</TableCell>
                                        <TableCell>{it.type}</TableCell>
                                      </TableRow>
                                    ))}
                                    {visibleFreeItems.map((it: { name: string; type: string }, idx: number) => (
                                      <TableRow key={`free-${it.name}-${idx}`}>
                                        <TableCell>フリー</TableCell>
                                        <TableCell>{it.name}</TableCell>
                                        <TableCell>{it.type}</TableCell>
                                      </TableRow>
                                    ))}
                                  </>
                                ) : (
                                  <TableRow>
                                    <TableCell colSpan={3} sx={{ color: 'text.secondary' }}>—</TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                ) : (
                  <Box sx={{ fontSize: 14, color: 'text.secondary' }}>データはありません。</Box>
                )}
              </Box>)}
              {isMismatchMode && (
                <DocumentMismatchPanel
                  departmentId={Number(division) || 0}
                  teamId={Number(team) || 0}
                  currentDocsetId={enlightDocsetId}
                  canEdit={canEdit}
                />
              )}
            </CardContent>
          </Card>
        </Box>
      </DialogContent>
      <DialogActions>
        {!isMismatchMode && (
        <Button
          variant="contained"
          onClick={isTeamMode ? () => setTeamConfirmOpen(true) : handleAcceptColumn}
          disabled={!canEdit || (isTeamMode ? submittingTeam : (submittingColumn || !enlightTeam))}
        >
          {acceptButtonLabel}
        </Button>
        )}
        <Button color="secondary" variant="outlined" onClick={onClose}>キャンセル</Button>
      </DialogActions>
      <Snackbar open={notifyOpen} message={notifyMsg} onClose={() => setNotifyOpen(false)} />
      <ConfirmDialog
        open={teamConfirmOpen}
        title="チーム連携を設定する"
        description="この操作では、Enlightチームとの紐付けのみが更新されます。既に発行済みの週報の再連携（新しい連携先への再登録）は、続く「Enlightカラムとの対応設定」を保存するまで行われません。よろしいですか？"
        confirmText="設定する"
        cancelText="キャンセル"
        processing={submittingTeam}
        onConfirm={() => {
          setTeamConfirmOpen(false);
          handleAcceptTeam();
        }}
        onCancel={() => setTeamConfirmOpen(false)}
      />
    </Dialog>
  );
};

export default EnlightTeamSettingDialog;
